import { createElement as _createElement } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropertyInputV2 from '../v2/components/PropertyInputV2';
import { buildPropertiesFormInitializationFailedState, buildPropertiesFormInitializingState, buildPropertiesFormInvalidState, buildPropertiesFormPendingState, buildPropertiesFormValidState, getUniqueFormInputs, getDependentFormProperties, getFormPropertyNames, getFormPropertiesDifference, isValidEnumOption } from './utils';
import usePropertiesFormApi from './usePropertiesFormApi';
import useConditionalPropertyRules from './useConditionalPropertyRules';
import useConditionalPropertyOptionsRules from './useConditionalPropertyOptionsRules';
import { isEnum } from 'customer-data-objects/property/PropertyIdentifier';
const emptyInputs = [];
const initializingState = buildPropertiesFormInitializingState();
const failedInitializingState = buildPropertiesFormInitializationFailedState();

const deriveFormStatus = (getFormValue, propertyDefinitions, objectTypeId, allPropertyValidationStates, inputs, enableConditionalPropertyOptions = false) => {
  const uniqueInputs = getUniqueFormInputs(inputs);
  const allValidationStates = [...allPropertyValidationStates.entries()];
  const isLoading = allValidationStates.some(([__, {
    loading
  }]) => loading);

  if (isLoading) {
    return buildPropertiesFormPendingState();
  }

  const errors = new Map(); // Collect required properties that don't have a value

  const requiredPropertiesWithNoValue = uniqueInputs.filter(input => input.isRequired && !getFormValue(input.property.name)).map(input => input.property);

  if (requiredPropertiesWithNoValue.length > 0) {
    errors.set('MISSING_REQUIRED_PROPERTY', requiredPropertiesWithNoValue);
  } // collecting properties with invalid status


  const propertiesWithInvalidStatus = allValidationStates.filter(([__, validationState]) => validationState.invalid).map(([propertyName]) => propertyDefinitions[propertyName]);

  if (propertiesWithInvalidStatus.length > 0) {
    errors.set('INVALID_PROPERTIES', propertiesWithInvalidStatus);
  }

  if (enableConditionalPropertyOptions) {
    // Conditional Property Option Check - collect inputs with value that is not an option in its property definition
    const propertiesWithInvalidOptions = uniqueInputs.filter(input => {
      const value = getFormValue(input.property.name);
      return isEnum(input.property) && !isValidEnumOption(objectTypeId, input.property, value);
    }).map(input => input.property);

    if (propertiesWithInvalidOptions.length > 0) {
      errors.set('INVALID_PROPERTIES', [...(errors.get('INVALID_PROPERTIES') || []), ...propertiesWithInvalidOptions]);
    }
  } // In case of property validations, there is an UNKNOWN state when the validation API call fails.
  // PropertyInput treats it as a valid scenario (saveable) as BE provides defense in depth if a bad value slips through.
  // PropertiesForm also treats those properties as saveable and does not explicitly list them with an UNKNOWN state


  return errors.size > 0 ? buildPropertiesFormInvalidState(errors) : buildPropertiesFormValidState();
};

const makeNewFormInput = ({
  propertyDefinitions,
  propertyDefinitionsWithFilteredOptionsActive,
  property,
  getFormValue,
  handleChange,
  handleValidationChange,
  objectId,
  objectTypeId,
  getIsUngated
}) => {
  const propertyName = property.propertyName;
  const propertyDefinition = propertyDefinitionsWithFilteredOptionsActive[propertyName] ? propertyDefinitionsWithFilteredOptionsActive[propertyName] : propertyDefinitions[propertyName]; // protects the form in case of misspelled or unavailable properties

  if (!propertyDefinition) {
    return undefined;
  }

  const dependents = [];

  for (const dependentProperty of property.dependents) {
    const dependentInput = makeNewFormInput({
      propertyDefinitions,
      propertyDefinitionsWithFilteredOptionsActive,
      property: dependentProperty,
      getFormValue,
      handleChange,
      handleValidationChange,
      objectId,
      objectTypeId,
      getIsUngated
    });

    if (dependentInput !== undefined) {
      dependents.push(dependentInput);
    }
  } // Setting value as undefined in an input will convert the input to uncontrolled state.
  // So, falling back to null.


  const value = getFormValue(propertyName) || null;
  return {
    property: propertyDefinition,
    isRequired: property.isRequired,
    dependents,
    render: additionalPropertyInputProps => {
      return /*#__PURE__*/_createElement(PropertyInputV2, Object.assign({}, additionalPropertyInputProps, {
        onChange: handleChange,
        onValidationChange: handleValidationChange,
        getAdditionalPropertyValue: getFormValue,
        enableConditionalPropertiesPanel: false,
        getIsUngated: getIsUngated,
        key: propertyDefinition.name,
        objectId: objectId,
        objectTypeId: objectTypeId,
        formControlProps: Object.assign({
          label: propertyDefinition.label
        }, additionalPropertyInputProps ? additionalPropertyInputProps.formControlProps : undefined, {
          required: property.isRequired
        }),
        property: propertyDefinition,
        value: value
      }));
    }
  };
};

const useNewPropertiesForm = props => {
  const {
    forceUngateForConditionalPropertyLogic,
    enableConditionalPropertyRules,
    enableConditionalPropertyOptions,
    fetchConditionalPropertyRules,
    fetchPropertyDefinitions,
    objectId,
    objectTypeId,
    onChange,
    properties,
    values,
    getAdditionalPropertyValue,
    getIsUngated,
    alwaysShowConditionalProperties = false
  } = props;
  const hasCpGate = forceUngateForConditionalPropertyLogic || (getIsUngated ? getIsUngated('CRM:ConditionalPropertyLogic') : false);
  const hasCpoGate = getIsUngated ? getIsUngated('CRM:ConditionalPropertyOptionsM2') : false;
  const [validationStates, setValidationStates] = useState(new Map());
  const [changedProperties, setChangedProperties] = useState(new Set());
  const [initialValuesCache, setInitialValuesCache] = useState({});
  const onChangeRef = useRef();
  onChangeRef.current = onChange;
  const valuesRef = useRef();
  valuesRef.current = values;
  const formPropertiesRef = useRef([]); // fetches all data and dispatches action with updated data

  const {
    loading,
    error,
    propertyDefinitions,
    conditionalPropertyRules,
    conditionalPropertyOptionsRules,
    cachedPropertyValues
  } = usePropertiesFormApi({
    objectTypeId,
    objectId,
    enableConditionalPropertyRules,
    enableConditionalPropertyOptions,
    fetchConditionalPropertyRules,
    fetchPropertyDefinitions
  }); // returns the current value for the given property. this value is shown in
  // the property input, used to calculate active CP and CPO rules, and used for
  // required-property validation. the current value is found by checking the
  // following sources, in order:
  //
  // 1. host app draft values given in the `values` prop
  // 2. host app values returned by `getAdditionalPropertyValue`
  // 3. cached values fetched from the api via `usePropertiesFormApi`

  const getFormValue = useCallback(propertyName => {
    if (Object.prototype.hasOwnProperty.call(values, propertyName)) {
      return values[propertyName];
    }

    const additionalPropertyValue = getAdditionalPropertyValue(propertyName, objectId, objectTypeId);

    if (additionalPropertyValue !== undefined) {
      return additionalPropertyValue;
    }

    if (Object.prototype.hasOwnProperty.call(cachedPropertyValues, propertyName)) {
      return cachedPropertyValues[propertyName];
    }

    return undefined;
  }, [values, objectId, objectTypeId, getAdditionalPropertyValue, cachedPropertyValues]);
  const formProperties = useConditionalPropertyRules({
    loading,
    error,
    enableConditionalPropertyRules: enableConditionalPropertyRules && hasCpGate,
    conditionalPropertyRules,
    objectTypeId,
    properties,
    propertyDefinitions,
    getFormValue,
    changedProperties,
    alwaysShowConditionalProperties
  });
  const propertyDefinitionsWithFilteredOptionsActive = useConditionalPropertyOptionsRules({
    loading,
    error,
    objectTypeId,
    enableConditionalPropertyOptions: enableConditionalPropertyOptions && hasCpoGate,
    conditionalPropertyOptionsRules,
    getFormValue,
    propertyDefinitions,
    formPropertyNames: getFormPropertyNames(formProperties)
  }); // wrapper to update current validation state to derive form status

  const handleValidationChange = useCallback(validationState => {
    setValidationStates(currentValidationState => new Map(currentValidationState).set(validationState.propertyName, validationState));
  }, []); // wrapper to update current values and pass to host apps

  const handleChange = useCallback(changes => {
    const propertyNames = Object.keys(changes);
    setChangedProperties(current => new Set([...current, ...propertyNames]));

    if (onChangeRef.current) {
      onChangeRef.current(Object.assign({}, valuesRef.current, {}, changes));
    }
  }, []);
  const inputs = useMemo(() => {
    if (loading || error) {
      return emptyInputs;
    }

    const newInputs = [];

    for (const property of formProperties) {
      const newFormInput = makeNewFormInput({
        propertyDefinitions,
        propertyDefinitionsWithFilteredOptionsActive,
        property,
        getFormValue,
        handleChange,
        handleValidationChange,
        objectId,
        objectTypeId,
        getIsUngated
      });

      if (newFormInput !== undefined) {
        newInputs.push(newFormInput);
      }
    }

    return newInputs;
  }, [loading, error, formProperties, propertyDefinitions, propertyDefinitionsWithFilteredOptionsActive, getFormValue, handleChange, handleValidationChange, objectId, objectTypeId, getIsUngated]);
  useEffect(() => {
    if (enableConditionalPropertyRules && hasCpGate) {
      const prevFormProperties = formPropertiesRef.current;
      formPropertiesRef.current = formProperties;

      if (prevFormProperties === formProperties) {
        // nothing to do; only check for changes if the list of dependents changed
        return;
      }

      const prevDependents = getDependentFormProperties(prevFormProperties);
      const dependents = getDependentFormProperties(formProperties);
      const allPropertyNames = getFormPropertyNames(formProperties);
      const {
        added,
        removed
      } = getFormPropertiesDifference(prevDependents, dependents); // when a dependent property appears:
      // * check to see if host app already provides a value (proactively)
      //   for the newly-visible property
      // * if yes, store that value in `initialValuesCache` so that when
      //   the property disappears, we can revert to the original value
      // * if not, store nothing in `initialValuesCache`

      if (added.length) {
        setInitialValuesCache(prev => {
          const next = Object.assign({}, prev);

          for (const propertyName of added) {
            if (Object.prototype.hasOwnProperty.call(values, propertyName)) {
              next[propertyName] = values[propertyName];
            }
          }

          return next;
        });
      } // when a dependent property disappears:
      // * if the property is present elsewhere on the form, do nothing
      // * check to see if the host app provides a value for the hidden
      //   property
      // * if yes, revert its value to whatever value was provided by the host
      //   app at the time the property appeared (if any)
      // * if no, remove the value from the draft values entirely


      if (removed.length) {
        // properties which no longer appear anywhere in the form
        const allOccurrencesRemoved = removed.filter(propertyName => !allPropertyNames.has(propertyName));
        let triggerOnChange = false;
        const newValues = Object.assign({}, valuesRef.current);

        for (const propertyName of allOccurrencesRemoved) {
          if (!Object.prototype.hasOwnProperty.call(newValues, propertyName)) {
            // host app did not provide a value for this property and the user did not edit it - no action needed
            continue;
          }

          if (Object.prototype.hasOwnProperty.call(initialValuesCache, propertyName)) {
            // host app provided a value for this property when it appeared - revert to that value
            newValues[propertyName] = initialValuesCache[propertyName];
          } else {
            // host app did not provide a value for this property but the user edited it - we are safe to throw away the value
            delete newValues[propertyName];
          }

          triggerOnChange = true;
        }

        if (triggerOnChange) {
          if (onChangeRef.current) {
            onChangeRef.current(newValues);
          }
        }

        setValidationStates(prev => {
          const next = new Map(prev); // if property is still in the form as a root input - no action needed

          for (const propertyName of allOccurrencesRemoved) {
            next.delete(propertyName);
          }

          return next;
        });
        setChangedProperties(prev => {
          const next = new Set(prev); // if property is still in the form as a root input - no action needed

          for (const propertyName of allOccurrencesRemoved) {
            next.delete(propertyName);
          }

          return next;
        });
        setInitialValuesCache(prev => {
          const next = Object.assign({}, prev);

          for (const propertyName of removed) {
            delete next[propertyName];
          }

          return next;
        });
      }
    }
  }, [enableConditionalPropertyRules, hasCpGate, formProperties, initialValuesCache, values]);
  const status = useMemo(() => deriveFormStatus(getFormValue, propertyDefinitions, objectTypeId, validationStates, inputs, enableConditionalPropertyOptions), [inputs, objectTypeId, propertyDefinitions, validationStates, getFormValue, enableConditionalPropertyOptions]);
  const reset = useCallback(() => {
    setValidationStates(new Map());
    setChangedProperties(new Set());
    setInitialValuesCache({});
    formPropertiesRef.current = [];
  }, []);

  if (error) {
    return {
      status: failedInitializingState,
      inputs: emptyInputs,
      reset
    };
  } else if (loading) {
    return {
      status: initializingState,
      inputs: emptyInputs,
      reset
    };
  } else {
    return {
      status,
      inputs,
      reset
    };
  }
};

export default useNewPropertiesForm;